/**
 * Virtual IPX server — in-process JavaScript port of caiiiycuk's
 * dosbox-ipx-server (https://github.com/caiiiycuk/dosbox-ipx-server),
 * the reference Novell-style router DOSBox-X expects on the wire when
 * networking is enabled.
 *
 * In a WebRTC P2P deployment we run this server *inside the host
 * browser tab*. The host's own DOSBox-X wasm registers as the first
 * client (slot 1); every remote peer that arrives over webrtcnet is
 * adopted as an additional client (slot 2, 3, …). DOSBox-X believes
 * it is talking to a real IPX server because — semantically — it is.
 *
 * Why faithfully replicate the C++ reference: DOOM/Blood/GTA1 elect a
 * "master" by lowest-MAC comparison, expect the server-assigned MAC
 * bytes 0..4 to be zero (Novell unrouted-segment convention), and pass
 * byte 5 (the slot) into their lobby tables as a tie-breaking node id.
 * Diverging from those wire conventions deadlocks the gathering
 * handshake — so we replicate the existing server's behaviour
 * byte-for-byte, removing the need for the synthetic-reply /
 * src-splice / self-loop / pending-broadcast workarounds the prior
 * bridge accumulated to fake one.
 */

// ────────────────────────────────────────────────────────────────────
// IPX header layout (packed, big-endian, 30 bytes total). Layout
// matches dosbox-x's `IPXHeader` (emulators/native/dosbox-x/include/
// ipx.h) and the wire serialisation in jsdos-ipx.cpp.
// ────────────────────────────────────────────────────────────────────
export const IPX_HEADER_SIZE  = 30;
export const IPX_DEST_NETWORK =  6; // 4 bytes
export const IPX_DEST_NODE    = 10; // 6 bytes
export const IPX_DEST_SOCKET  = 16; // 2 bytes
export const IPX_SRC_NETWORK  = 18; // 4 bytes
export const IPX_SRC_NODE     = 22; // 6 bytes
export const IPX_SRC_SOCKET   = 28; // 2 bytes

/** Socket dosbox-x's `_ConnectToServer` uses to register against the IPX server. */
const IPX_SOCKET_REGISTRATION = 0x0002;

/**
 * Network number a real Novell IPX server assigns to clients on a
 * single unrouted physical segment. dosbox-x copies it into
 * `localIpxAddr.netnum`; both peers must share the same value or
 * game-layer netnum checks reject each other's packets.
 */
const IPX_NETWORK_ID = 0x00000001;

export type IpxRouteOutcome =
    | "registration"
    | "broadcast"
    | "unicast"
    | "self-loop"
    | "no-target"
    | "too-short"
    | "no-sender";

/**
 * Sink the server invokes to deliver a packet to a registered client.
 *
 * For the host's own wasm the sink dispatches `wc-net-received`; for a
 * remote WebRTC peer the sink calls `net.sendBinary(packet, peerId)`.
 * Implementations must NOT retain the `packet` buffer past the call —
 * sender side may reuse it on the next iteration.
 */
export type IpxSink = (packet: Uint8Array) => void;

interface IpxClient {
    id: string;
    /** 6-byte Novell-style MAC: 00:00:00:00:00:<slot>. */
    mac: Uint8Array;
    sink: IpxSink;
}

/**
 * Render a 6-byte MAC as a lowercase colon-separated string suitable
 * for use as a Map key. The Uint8Array itself can't be used because
 * Map uses reference equality on objects.
 */
function macKey(mac: Uint8Array, offset = 0): string {
    return mac[offset].toString(16).padStart(2, "0") + ":"
         + mac[offset + 1].toString(16).padStart(2, "0") + ":"
         + mac[offset + 2].toString(16).padStart(2, "0") + ":"
         + mac[offset + 3].toString(16).padStart(2, "0") + ":"
         + mac[offset + 4].toString(16).padStart(2, "0") + ":"
         + mac[offset + 5].toString(16).padStart(2, "0");
}

/**
 * dosbox-x's `_ConnectToServer` sends a 30-byte packet on socket
 * 0x0002 with all-zero destination network and node to ask the server
 * "what's my MAC?". A real IPX server replies with the assigned MAC.
 */
export function isRegistrationRequest(data: Uint8Array): boolean {
    if (data.byteLength !== IPX_HEADER_SIZE) return false;
    if (data[IPX_DEST_SOCKET]     !== 0x00)  return false;
    if (data[IPX_DEST_SOCKET + 1] !== 0x02)  return false;
    for (let i = 0; i < 4; i++) {
        if (data[IPX_DEST_NETWORK + i] !== 0) return false;
    }
    for (let i = 0; i < 6; i++) {
        if (data[IPX_DEST_NODE + i] !== 0) return false;
    }
    return true;
}

function isBroadcastDst(data: Uint8Array): boolean {
    return data[IPX_DEST_NODE    ] === 0xff
        && data[IPX_DEST_NODE + 1] === 0xff
        && data[IPX_DEST_NODE + 2] === 0xff
        && data[IPX_DEST_NODE + 3] === 0xff
        && data[IPX_DEST_NODE + 4] === 0xff
        && data[IPX_DEST_NODE + 5] === 0xff;
}

/**
 * Build the registration response a real Novell IPX server would
 * send: a 30-byte packet with the client's assigned netnum + MAC in
 * the destination half, the registration socket, and zero src half.
 * dosbox-x's `_ConnectToServer` copies `dest.network` into
 * `localIpxAddr.netnum` and `dest.addr.byNode.node` into
 * `localIpxAddr.netnode`; everything else is ignored.
 *
 * Exported so the bridge can pre-build a welcome reply at bind time
 * (before the wasm has sent its REG-REQUEST), which side-steps a
 * race where the dosbox-x server-mode init skips REG-REQUEST entirely
 * and where the guest's first sendBinary fires before the WebRTC
 * datachannel is open (packet lost). With a locally-dispatched reply
 * dosbox-x has the right localIpxAddr.netnode before any wire send.
 */
export function buildRegistrationReply(mac: Uint8Array): Uint8Array {
    const reply = new Uint8Array(IPX_HEADER_SIZE);
    const view  = new DataView(reply.buffer);
    view.setUint16(0,                    0xffff,           false); // checksum
    view.setUint16(2,                    IPX_HEADER_SIZE,  false); // length
    view.setUint32(IPX_DEST_NETWORK,     IPX_NETWORK_ID,   false);
    reply.set(mac, IPX_DEST_NODE);
    view.setUint16(IPX_DEST_SOCKET,      IPX_SOCKET_REGISTRATION, false);
    return reply;
}

// ────────────────────────────────────────────────────────────────────
// Bridge control frames (host ↔ guest slot handshake).
//
// These ride the SAME webrtcnet binary channel as IPX packets but are
// NOT IPX: every IPX frame on this wire begins with checksum 0xffff
// (byte 0 == 0xff), so a leading 0x00 unambiguously marks a control
// frame. 6 bytes: [0x00, 'M', 'P', type, slot, 0x00].
//
// Why they exist: a guest must stamp its wasm IPX node id with the slot
// the HOST assigned (VirtualIpxServer.allocSlot), not a hardcoded 2 —
// otherwise every guest collides on node 2 and 3+ player lobbies never
// separate. SLOT_REQUEST (guest→host, retried until answered) asks for
// the slot; SLOT_ASSIGN (host→guest) carries it back.
// ────────────────────────────────────────────────────────────────────
export const MP_CTRL_SIZE         = 6;
export const MP_CTRL_SLOT_REQUEST = 0x01;
export const MP_CTRL_SLOT_ASSIGN  = 0x02;

export function isControlFrame(d: Uint8Array): boolean {
    return d.byteLength === MP_CTRL_SIZE
        && d[0] === 0x00 && d[1] === 0x4d /* M */ && d[2] === 0x50 /* P */;
}

export function controlType(d: Uint8Array): number {
    return d[3];
}

export function controlSlot(d: Uint8Array): number {
    return d[4];
}

export function buildSlotRequest(): Uint8Array {
    return new Uint8Array([0x00, 0x4d, 0x50, MP_CTRL_SLOT_REQUEST, 0x00, 0x00]);
}

export function buildSlotAssign(slot: number): Uint8Array {
    return new Uint8Array([0x00, 0x4d, 0x50, MP_CTRL_SLOT_ASSIGN, slot & 0xff, 0x00]);
}

/**
 * Tiny IPX router that mirrors `dosbox-ipx-server`. Holds a flat list
 * of clients keyed by id and a MAC → client side index for O(1)
 * unicast routing. Broadcast is O(N) over clients minus the sender.
 */
export class VirtualIpxServer {
    private readonly clients = new Map<string, IpxClient>();
    private readonly byMac   = new Map<string, IpxClient>();

    /**
     * Register a new client. Returns the assigned MAC (which the
     * caller may need for diagnostic output; the server uses it
     * internally to route packets back to this client).
     *
     * Idempotent: re-registering an existing id is a no-op and
     * returns the previously assigned MAC, so the recv pump can call
     * this on every inbound packet without bookkeeping.
     */
    register(id: string, sink: IpxSink): Uint8Array {
        const existing = this.clients.get(id);
        if (existing) return existing.mac;
        const slot = this.allocSlot();
        const mac  = new Uint8Array([0, 0, 0, 0, 0, slot & 0xff]);
        const client: IpxClient = { id, mac, sink };
        this.clients.set(id, client);
        this.byMac.set(macKey(mac), client);
        return mac;
    }

    /**
     * Lowest unused slot id starting at 1. Recycling matters because
     * DOS-era IPX games carry a fixed per-slot player table — GTA1
     * caps at MAX_PLAYERS=2, DOOM at 4, Blood at 8 — and a netnode
     * outside that range is silently dropped. Without recycling, the
     * second guest of a session lands on slot 3 (host=1, departed
     * guest=2 was never reclaimed) which sits past the GTA1 table and
     * the lobby never registers the join. Walking the live MAC byte
     * is O(N) on player count (≤8 in any DOS title we ship) and
     * leaves a single source of truth, so we avoid a parallel free
     * list that could desync from `clients`.
     */
    private allocSlot(): number {
        const used = new Set<number>();
        for (const c of this.clients.values()) used.add(c.mac[5]);
        let slot = 1;
        while (used.has(slot)) slot++;
        return slot;
    }

    unregister(id: string): void {
        const client = this.clients.get(id);
        if (!client) return;
        this.clients.delete(id);
        this.byMac.delete(macKey(client.mac));
    }

    has(id: string): boolean {
        return this.clients.has(id);
    }

    size(): number {
        return this.clients.size;
    }

    /**
     * Snapshot of currently registered client ids. Returned as an
     * array (not the live Map iterator) so callers can safely mutate
     * the server — e.g. unregister stragglers — while walking it.
     */
    ids(): string[] {
        return Array.from(this.clients.keys());
    }

    /**
     * Handle a packet a client has sent to the server.
     *
     *   • REG-REQUEST (socket 0x0002, all-zero dst.network/dst.node)
     *     → synthesise REG-REPLY back to the sender, do NOT forward.
     *   • Broadcast (dst.node = FF:FF:FF:FF:FF:FF)
     *     → fan out to every client except the sender. The sender's
     *       own wasm short-circuits broadcasts locally in
     *       `IPX_SendPacket` before the bytes reach us, so we never
     *       reflect them back.
     *   • Unicast → forward to the client whose MAC matches dst.node.
     *     If dst.node == sender's MAC (self-loopback) the client
     *     receives its own packet — matches what a real IPX network
     *     does when a station addresses itself.
     *   • Unknown MAC → drop. The wasm has the right to retry; once
     *     the target peer arrives we'll route the next send.
     */
    deliver(senderId: string, data: Uint8Array): IpxRouteOutcome {
        const sender = this.clients.get(senderId);
        if (!sender) return "no-sender";

        if (isRegistrationRequest(data)) {
            sender.sink(buildRegistrationReply(sender.mac));
            return "registration";
        }

        if (data.byteLength < IPX_HEADER_SIZE) return "too-short";

        if (isBroadcastDst(data)) {
            for (const c of this.clients.values()) {
                if (c.id !== sender.id) c.sink(data);
            }
            return "broadcast";
        }

        const target = this.byMac.get(macKey(data, IPX_DEST_NODE));
        if (!target) return "no-target";
        target.sink(data);
        return target.id === sender.id ? "self-loop" : "unicast";
    }
}
